import type React from 'react';

import { Grid } from '@change/ds-layout';

import { ClientRender } from '@change-corgi/core-react/ssr/render';
import { TrackingContext } from '@change-corgi/core-react/tracking';

import { SetPageStateValue } from '#src/shared/pageState';

import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import { MobileSignFormContextProvider } from '#src/app/pages/petitionDetails/shared/components';
import {
	usePetitionPageStateData,
	useTrackPageView,
	useShowNewlyCreatedPetitions,
	useShowSuggestedVictoriousPetitions,
	useTrackPromotedView,
	PageScrollPositionProvider,
	StickyActionBarHeightProvider,
} from '#src/app/pages/petitionDetails/shared/hooks';
import { BeforeSuggestedPetitionsWaypoint } from '#src/app/pages/petitionDetails/shared/waypoints';
import { AppDownloadBottomBanner } from '#src/app/shared/components/banners/appDownload';
import { PetitionUnderReviewBanner } from '#src/app/shared/components/banners/petitionUnderReview';
import { MembershipBottomBanner } from '#src/app/shared/components/membership';
import { SuggestedPetitionsLazy } from '#src/app/shared/components/suggestedPetitionsLazy';

import { DmFeedBanner } from '../DmFeedBanner';
import { ExpiredSessionBanner } from '../ExpiredSessionBanner';
import { Heading } from '../Heading';
import { Main } from '../Main';
import { useVideoFirstExperiment } from '../Main/hooks/useVideoFirstExperiment';
import { NativeShareMobile } from '../NativeShareMobile';
import { RedirectUnpublished } from '../RedirectUnpublished';
import { ScrollTrackingWaypoint } from '../ScrollTrackingWaypoint';
import { StrengthTips } from '../StrengthTips';

export const Details: React.FC<EmptyProps> = () => {
	useTrackPageView();
	useTrackPromotedView();
	const showSuggestedVictoriousPetitions = useShowSuggestedVictoriousPetitions();
	const showNewlyCreated = useShowNewlyCreatedPetitions();
	const {
		data: { petition, iOSBannerEnabled, androidBannerEnabled, desktopBannerEnabled },
	} = usePetitionDetailsPageData();
	const { photo } = petition;
	const petitionPageStateData = usePetitionPageStateData();
	const {
		data: { ownPetition },
	} = usePetitionDetailsPageUserData();
	const { videoFirstSourceUrl } = useVideoFirstExperiment();
	const underReview = petition.status === 'UNDER_REVIEW';

	return (
		<div data-testid="details-container" className="mb-8">
			<ExpiredSessionBanner />
			{underReview && <PetitionUnderReviewBanner />}
			<RedirectUnpublished />
			{/* FIXME: for consistency this should be rendered in DetailsWithContext with the other providers. */}
			<PageScrollPositionProvider>
				<StickyActionBarHeightProvider>
					<SetPageStateValue name="petition" value={petitionPageStateData} />
					<DmFeedBanner />
					<StrengthTips />
					{/* NativeShareMobile is client-only because it checks the user-agent, which is not available in SSR */}
					<ClientRender>
						<NativeShareMobile />
					</ClientRender>

					<Grid className="lg:!col-gap-6 lg:!row-gap-0 relative gap-0! px-0 lg:max-w-[1376px] lg:grid-cols-14!">
						<div className="hidden md:col-start-1 md:col-end-12 md:block lg:col-start-2 lg:col-end-14">
							{photo === null && !videoFirstSourceUrl ? <Heading /> : null}
						</div>
						<div className="col-span-full">
							<MobileSignFormContextProvider>
								<Main />
							</MobileSignFormContextProvider>
						</div>
					</Grid>
					<BeforeSuggestedPetitionsWaypoint />
					<ScrollTrackingWaypoint name="scroll_other_petitions_visible" />
					<SuggestedPetitionsLazy
						showVictorious={showSuggestedVictoriousPetitions}
						showNewlyCreated={showNewlyCreated}
						petitionSlugOrId={petition.id}
						channel="petition_footer"
						sourceLocation="petition_footer"
					/>
				</StickyActionBarHeightProvider>
			</PageScrollPositionProvider>

			{/* MembershipBottomBanner is client-only because it checks the user-agent, which is not available in SSR */}
			<ClientRender>
				<MembershipBottomBanner slug={petition.slug} sourceLocation="petition_membership_popup" />
			</ClientRender>
			<TrackingContext properties={{ petition_id: `petition_${petition.id}` }}>
				<AppDownloadBottomBanner
					sourceLocation="petition"
					ownPetition={ownPetition}
					iOSBannerEnabled={iOSBannerEnabled}
					androidBannerEnabled={androidBannerEnabled}
					desktopBannerEnabled={desktopBannerEnabled}
				/>
			</TrackingContext>
		</div>
	);
};
